import React, { Fragment } from "react";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";

import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import LooksOneIcon from "@material-ui/icons/LooksOne";
import LooksTwoIcon from "@material-ui/icons/LooksTwo";
import LooksThreeIcon from "@material-ui/icons/Looks3";
import LooksFourIcon from "@material-ui/icons/Looks4";
import LooksFiveIcon from "@material-ui/icons/Looks5";
import LooksSixIcon from "@material-ui/icons/Looks6";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatCodeIcon from "@material-ui/icons/Code";

import "./RichTextEditor.css";

const EMPTY_HTML = "<p></p>";

// Link extension that preserves the legacy UX: Ctrl/Cmd+K opens a window.prompt
// to insert or clear a link on the current selection.
const promptForLink = editor => {
    const link = window.prompt("Paste the link -");
    if (!link) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return true;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: link }).run();
    return true;
};

const LinkWithShortcut = Link.extend({
    addKeyboardShortcuts() {
        return {
            "Mod-k": () => promptForLink(this.editor)
        };
    }
});

class RichTextEditor extends React.Component {
    constructor(props) {
        super(props);
        this.editor = null;
        this.editorElement = React.createRef();
        // forceUpdate counter so the toolbar reflects the current active marks/blocks
        this.state = { tick: 0 };
    }

    componentDidMount() {
        const initialContent = this.props.sample || this.props.value || EMPTY_HTML;

        this.editor = new Editor({
            element: this.editorElement.current,
            editable: !this.props.readOnly,
            content: initialContent,
            extensions: [
                StarterKit.configure({
                    heading: { levels: [1, 2, 3, 4, 5, 6] }
                }),
                Underline,
                TextStyle,
                LinkWithShortcut.configure({
                    openOnClick: false,
                    autolink: true,
                    HTMLAttributes: {
                        rel: "noopener noreferrer",
                        target: "_blank"
                    }
                }),
                Placeholder.configure({
                    placeholder: this.props.placeholder || ""
                })
            ],
            onUpdate: ({ editor }) => {
                if (this.props.onChange) {
                    this.props.onChange(editor.getHTML());
                }
            },
            onTransaction: () => {
                // re-render so toolbar active states stay in sync
                this.setState(prev => ({ tick: prev.tick + 1 }));
            }
        });

        // Match legacy behaviour: when a sample template is supplied on mount,
        // push it back up through onChange so the store is populated.
        if (this.props.sample && this.props.onChange) {
            this.props.onChange(this.editor.getHTML());
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.editor) return;

        if (prevProps.readOnly !== this.props.readOnly) {
            this.editor.setEditable(!this.props.readOnly);
        }

        // A new sample template was supplied: load it and emit the change.
        if (this.props.sample && this.props.sample !== prevProps.sample) {
            this.editor.commands.setContent(this.props.sample, false);
            if (this.props.onChange) {
                this.props.onChange(this.editor.getHTML());
            }
            return;
        }

        // The reset flows mirror the previous Draft.js implementation so the
        // consuming components (resetEditor / onChangeEditor / isVenueChange)
        // keep working without any changes.
        if (this.props.onChangeEditor && this.props.value !== prevProps.value && this.props.resetEditor) {
            this.props.onChangeEditor(false);
            this.syncContentFromValue();
        } else if (this.props.onChangeEditor && this.props.isVenueChange) {
            this.props.onChangeEditor(false);
            this.syncContentFromValue();
        } else if (this.props.value !== prevProps.value && this.props.resetEditor) {
            this.syncContentFromValue();
        } else if (this.props.value === EMPTY_HTML && this.props.resetEditor) {
            if (this.props.value !== prevProps.value) {
                this.syncContentFromValue();
            }
        }

        if (this.props.onChangeEditor) {
            this.props.onChangeEditor(false);
        }
    }

    componentWillUnmount() {
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
    }

    syncContentFromValue = () => {
        const content = this.props.value || EMPTY_HTML;
        // Avoid clobbering the selection/cursor when nothing actually changed.
        if (this.editor.getHTML() !== content) {
            this.editor.commands.setContent(content, false);
        }
    };

    insertLink = () => {
        if (this.editor) {
            promptForLink(this.editor);
        }
    };

    toggleInlineStyle = style => {
        if (!this.editor) return;
        const chain = this.editor.chain().focus();
        switch (style) {
            case "BOLD":
                chain.toggleBold().run();
                break;
            case "ITALIC":
                chain.toggleItalic().run();
                break;
            case "UNDERLINE":
                chain.toggleUnderline().run();
                break;
            default:
                break;
        }
    };

    toggleBlockType = blockType => {
        if (!this.editor) return;
        const chain = this.editor.chain().focus();
        switch (blockType) {
            case "header-one":
                chain.toggleHeading({ level: 1 }).run();
                break;
            case "header-two":
                chain.toggleHeading({ level: 2 }).run();
                break;
            case "header-three":
                chain.toggleHeading({ level: 3 }).run();
                break;
            case "header-four":
                chain.toggleHeading({ level: 4 }).run();
                break;
            case "header-five":
                chain.toggleHeading({ level: 5 }).run();
                break;
            case "header-six":
                chain.toggleHeading({ level: 6 }).run();
                break;
            case "blockquote":
                chain.toggleBlockquote().run();
                break;
            case "unordered-list-item":
                chain.toggleBulletList().run();
                break;
            case "ordered-list-item":
                chain.toggleOrderedList().run();
                break;
            case "code-block":
                chain.toggleCodeBlock().run();
                break;
            default:
                break;
        }
    };

    isBlockActive = blockType => {
        if (!this.editor) return false;
        switch (blockType) {
            case "header-one":
                return this.editor.isActive("heading", { level: 1 });
            case "header-two":
                return this.editor.isActive("heading", { level: 2 });
            case "header-three":
                return this.editor.isActive("heading", { level: 3 });
            case "header-four":
                return this.editor.isActive("heading", { level: 4 });
            case "header-five":
                return this.editor.isActive("heading", { level: 5 });
            case "header-six":
                return this.editor.isActive("heading", { level: 6 });
            case "blockquote":
                return this.editor.isActive("blockquote");
            case "unordered-list-item":
                return this.editor.isActive("bulletList");
            case "ordered-list-item":
                return this.editor.isActive("orderedList");
            case "code-block":
                return this.editor.isActive("codeBlock");
            default:
                return false;
        }
    };

    isInlineActive = style => {
        if (!this.editor) return false;
        switch (style) {
            case "BOLD":
                return this.editor.isActive("bold");
            case "ITALIC":
                return this.editor.isActive("italic");
            case "UNDERLINE":
                return this.editor.isActive("underline");
            default:
                return false;
        }
    };

    render() {
        return (
            <div className="RichEditor-root" id={this.props.id}>
                {!this.props.hideMenu && (
                    <Fragment>
                        <BlockStyleControls
                            isActive={this.isBlockActive}
                            onToggle={this.toggleBlockType}
                        />
                        <InlineStyleControls
                            isActive={this.isInlineActive}
                            onToggle={this.toggleInlineStyle}
                        />
                    </Fragment>
                )}
                <div className={this.props.hideMenu ? "" : "RichEditor-editor"}>
                    <div ref={this.editorElement} />
                </div>
            </div>
        );
    }
}

function renderIcon(label) {
    switch (label) {
        case "H1":
            return <LooksOneIcon />;
        case "H2":
            return <LooksTwoIcon />;
        case "H3":
            return <LooksThreeIcon />;
        case "H4":
            return <LooksFourIcon />;
        case "H5":
            return <LooksFiveIcon />;
        case "H6":
            return <LooksSixIcon />;
        case "Blockquote":
            return <FormatQuoteIcon />;
        case "UL":
            return <FormatListBulletedIcon />;
        case "OL":
            return <FormatListNumberedIcon />;
        case "Code Block":
            return <FormatCodeIcon />;
        case "Bold":
            return <FormatBoldIcon />;
        case "Italic":
            return <FormatItalicIcon />;
        case "Underline":
            return <FormatUnderlinedIcon />;
        default:
            return label;
    }
}

class StyleButton extends React.Component {
    constructor() {
        super();
        this.onToggle = e => {
            e.preventDefault();
            this.props.onToggle(this.props.style);
        };
    }

    render() {
        let className = "RichEditor-styleButton";
        if (this.props.active) {
            className += " RichEditor-activeButton";
        }

        return (
            <span className={className} onMouseDown={this.onToggle}>
                {renderIcon(this.props.label)}
            </span>
        );
    }
}

const BLOCK_TYPES = [
    { label: "H1", style: "header-one" },
    { label: "H2", style: "header-two" },
    { label: "H3", style: "header-three" },
    { label: "H4", style: "header-four" },
    { label: "H5", style: "header-five" },
    { label: "H6", style: "header-six" },
    { label: "Blockquote", style: "blockquote" },
    { label: "UL", style: "unordered-list-item" },
    { label: "OL", style: "ordered-list-item" },
    { label: "Code Block", style: "code-block" }
];

const BlockStyleControls = props => {
    return (
        <div className="RichEditor-controls">
            {BLOCK_TYPES.map(type => (
                <StyleButton
                    key={type.label}
                    active={props.isActive(type.style)}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            ))}
        </div>
    );
};

const INLINE_STYLES = [
    { label: "Bold", style: "BOLD" },
    { label: "Italic", style: "ITALIC" },
    { label: "Underline", style: "UNDERLINE" }
];

const InlineStyleControls = props => {
    return (
        <div className="RichEditor-controls">
            {INLINE_STYLES.map(type => (
                <StyleButton
                    key={type.label}
                    active={props.isActive(type.style)}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            ))}
        </div>
    );
};

export default RichTextEditor;
